import { cn } from '@/lib/cn'
import { Text } from '@/components/ui/Text'

interface SignupStepperProps {
  currentStep: 1 | 2 | 3
  className?: string
}

const STEPS: { label: string }[] = [
  { label: 'Account' },
  { label: 'Workspace' },
  { label: 'Verify email' },
]

export function SignupStepper({ currentStep, className }: SignupStepperProps) {
  return (
    <nav aria-label="Signup progress" className={cn('mb-8', className)}>
      {/* Now sits under the page's own <h1> (PRD AC11) rather than opening the column itself, so
          this reads as a secondary section label, not the page's only intro text — tightened
          spacing/tracking accordingly rather than treated as the top of the right panel. */}
      <Text variant="label-sm" color="muted" className="mb-3 uppercase tracking-[0.08em] font-semibold">
        Getting started
      </Text>
      <ol className="grid grid-cols-3 gap-0" role="list">
        {STEPS.map((step, i) => {
          const stepNumber = (i + 1) as 1 | 2 | 3
          // currentStep=3 (the account-created screen — SignupPage only ever passes 3 there) means
          // "Account" and "Workspace" are genuinely done and "Verify email" is the CURRENT step, not
          // a third done step: the user hasn't clicked the emailed link yet. This is the same
          // done-vs-active distinction already used for steps 1/2 during the live flow — no separate
          // "all complete" state exists, because one never should have (the tick must stay truthful
          // to what the screen's own copy says is still outstanding).
          const isDone = stepNumber < currentStep
          const isActive = stepNumber === currentStep

          return (
            <li
              key={step.label}
              className="relative flex items-center gap-1.5 min-w-0"
              aria-current={isActive ? 'step' : undefined}
            >
              {/* Connector line between steps */}
              {i > 0 && (
                <div
                  className="absolute left-0 right-0 top-3 h-px bg-border -translate-y-px"
                  aria-hidden="true"
                />
              )}

              <div className="relative z-10 flex items-center gap-1.5 min-w-0">
                {/* Step dot */}
                <div
                  className={cn(
                    'flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border text-[8px] font-semibold',
                    isDone && 'border-success-muted bg-success-subtle text-success',
                    isActive && 'border-primary bg-primary text-white shadow-[0_0_0_3px_oklch(from_var(--color-primary)_l_c_h_/_0.15)]',
                    !isDone && !isActive && 'border-border bg-surface text-text-muted',
                  )}
                  aria-hidden="true"
                >
                  {isDone ? '✓' : stepNumber}
                </div>

                {/* Step label */}
                <Text
                  variant="label-sm"
                  color={isActive ? 'default' : 'muted'}
                  className={cn(
                    'truncate',
                    isActive && 'font-bold',
                  )}
                >
                  {step.label}
                </Text>
              </div>
            </li>
          )
        })}
      </ol>
      <Text variant="body-sm" color="muted" className="mt-1.5">
        {currentStep === 1 && 'Create your account first. Workspace setup comes next.'}
        {currentStep === 2 && 'Tell us who the organization is and how it manages legal entities.'}
        {currentStep === 3 && "Check your inbox to verify your email — that's the last step."}
      </Text>
    </nav>
  )
}
